import { IController, IWindowService } from 'angular';
import { IStateService } from 'angular-ui-router';
import * as _ from 'lodash';
import { ImSchool } from 'Src/ng2/shared/services/im-models/im-school';
import { GraduationPlanTransfer } from '../../../../../../ng2/shared/constants/graduation-plan-transfer.constant';
import { IWizardFilterActionsOptions } from '../../../components/wizard-filter-actions/wizard-filter-actions.typings';
import { ISdcStore } from './../../../../../sdc/flux/sdc-store.interface';
import {
  CreditGapsPriorityGroupingsTransferService,
  IPriorityGroupDefinition,
} from './../../../../../shared/services/credit-gaps-priority-groupings/credit-gaps-priority-groupings-transfer.service';

type TToggleValue = 'creditMaxGapsNoPlan' | 'creditMaxGaps';

// remember : creditMaxGaps === `reg gaps` creditGapsMaxNoPlan === `unaddressed gaps`
export class CreditGapsTransferTableCtrl implements IController {
  static inject: string[] = ['$scope', 'SDCStore', 'ImSchool', 'ObjectCache', '$state', '$window', 'UtilitiesService'];

  public uiReady: boolean = false;
  public filteredStudents: any[]; // sdc students
  public tableToggle: TToggleValue = 'creditMaxGaps';
  public tableHeaders: string[];
  public table;
  public totalsData;
  private tableData;
  private creditGapThresholdBuckets;
  private priorityGrouping: IPriorityGroupDefinition[];
  public options: IWizardFilterActionsOptions;
  private nextFourGradDates;
  public studentWithUnaddressedGaps: string[];
  public studentWithGaps: string[];

  private creditGapThresholdsBaseObj = {
    '16+': {
      human: '16.51+',
      buckets: {
        Total: [],
      },
      color: 'red',
    },
    '11To16': {
      human: '11.01-16.5',
      buckets: {
        Total: [],
      },
      color: 'pink',
    },
    '5To11': {
      human: '5.51-11.0',
      buckets: {
        Total: [],
      },
      color: 'orange',
    },
    '1To5': {
      human: '0.01-5.5',
      buckets: {
        Total: [],
      },
      color: 'yellow',
    },
    zero: {
      human: '0',
      buckets: {
        Total: [],
      },
      color: 'green',
    },
    // Total row:
    totals: {
      total: [],
    },
  };

  constructor(
    private $scope,
    private SDCStore: ISdcStore,
    private ImSchool: ImSchool,
    private ObjectCache,
    private $state: IStateService,
    private $window: IWindowService,
    private CreditGapsPriorityGroupingsTransferService: CreditGapsPriorityGroupingsTransferService,
    private UtilitiesService,
  ) {
    this.options = {
      columnKeys: ['gradPlan', 'creditMaxGaps', 'creditMaxGapsNoPlan'],
    };
  }

  $onInit(): void {
    this.$scope.$listenTo(this.SDCStore, ['ui', 'isLoading'], () => {
      const isLoading = this.SDCStore.getUiState().isLoading;
      if (!isLoading) {
        this.setUi();
        this.setListeners();
        this.uiReady = true;
      }
    });
  }

  setUi(): void {
    this.filteredStudents = this.SDCStore.getFilteredFlattenedStudents();
    this.nextFourGradDates = this.ImSchool.getNextFourGradDatesForTransfer();
    // removes `Students with no unaddressed gaps` from grouping
    this.priorityGrouping = _.dropRight(
      this.CreditGapsPriorityGroupingsTransferService.getPriorityGroupsByPlannedGradDateFilter(null),
      1,
    );
    this.creditGapThresholdBuckets = this.generateCreditGapBuckets();
    this.tableHeaders = this.generateTableHeaders();
    this.updateTable();

    this.studentWithUnaddressedGaps = _.reduce(
      this.filteredStudents,
      (result, { creditMaxGapsNoPlan, studentId }) => {
        if (creditMaxGapsNoPlan < Math.abs(0)) result.push(studentId);
        return result;
      },
      [],
    );

    this.studentWithGaps = _.reduce(
      this.filteredStudents,
      (result, { creditMaxGaps, studentId }) => {
        if (creditMaxGaps < Math.abs(0)) result.push(studentId);
        return result;
      },
      [],
    );
  }

  updateTable(): void {
    // includes data not needed in view
    this.tableData = this.buildCreditGapsTable(this.filteredStudents, this.tableToggle);
    // cleaned for view
    this.table = _.omit(this.tableData, 'totals');
    this.totalsData = _.get(this.tableData, 'totals');
  }

  private setListeners(): void {
    this.$scope.$listenTo(this.SDCStore, ['filteredFlattenedStudents'], this.setUi.bind(this));
    this.$scope.$listenTo(this.SDCStore, ['flattenedStudents'], this.setUi.bind(this));
    this.$scope.$listenTo(this.SDCStore, ['filter'], this.setUi.bind(this));
    this.$scope.$listenTo(this.SDCStore, ['school'], this.setUi.bind(this));
  }

  private generateTableHeaders(): string[] {
    return _.reduce(
      this.priorityGrouping,
      (result: any, group) => {
        const { plannedGradDateFilter: gradDate } = group;
        result.push(gradDate);
        return result;
      },
      ['Total'],
    ); // ensure column order is correct
  }

  private generateCreditGapBuckets() {
    return _.forOwn(this.creditGapThresholdsBaseObj, (row: any, k) => {
      if (k === 'totals') _.each(this.priorityGrouping, group => (row[group.plannedGradDateFilter] = []));
      else _.each(this.priorityGrouping, group => (row.buckets[group.plannedGradDateFilter] = []));
    });
  }

  private mapGradPlanToTableHeaders(gradPlan: string) {
    // handle plans that don't map cleanly columnHeaders / baseObj buckets
    if (gradPlan === GraduationPlanTransfer.PLAN_IN_PAST.humanName) {
      return _.find(this.priorityGrouping, { key: 'PLAN_INCOMPLETE' }).plannedGradDateFilter;
    }
    if (gradPlan === GraduationPlanTransfer.NON_GRADUATE.humanName) {
      return _.find(this.priorityGrouping, { key: 'PLANNED_NON_GRAD' }).plannedGradDateFilter;
    }
    // find in GraduationPlanTransfer constant - sanity check
    const planObject = _.find(GraduationPlanTransfer, { humanName: gradPlan });
    // is it `planned` with date or not
    if (!planObject.date) return planObject.humanName;
    // if so, is that date beyond the next four grad dates?
    else if (planObject.date && _.find(this.nextFourGradDates, { humanName: planObject.date })) {
      // no
      return planObject.date;
    } else {
      // yes
      return _.find(this.priorityGrouping, { key: 'PLANNED_BEYOND_4' }).plannedGradDateFilter;
    }
  }

  private buildCreditGapsTable(students: any[], toggleValue: TToggleValue) {
    // maintains a clean copy of the buckets on each filter change
    const creditGapThresholdBuckets = this.UtilitiesService.copyPOJO(this.creditGapThresholdBuckets);
    // wrapper for repeated functionality in .each
    const setValues = (studentId, gradPlan, creditThreshold): void => {
      creditGapThresholdBuckets[creditThreshold].buckets[gradPlan].push(studentId);
      creditGapThresholdBuckets[creditThreshold].buckets.Total.push(studentId);
      creditGapThresholdBuckets.totals[gradPlan].push(studentId);
      // always push, this is the total row:
      creditGapThresholdBuckets.totals.total.push(studentId);
    };
    _.each(students, student => {
      // const set dynamically via table toggle
      const creditGaps = student[toggleValue];
      let { studentId, gradPlan } = student;
      const mappedGradPlan = this.mapGradPlanToTableHeaders(gradPlan);
      // bucket logic
      if (creditGaps === Math.abs(0)) setValues(studentId, mappedGradPlan, 'zero');
      else if (creditGaps < 0 && creditGaps >= -5.5) setValues(studentId, mappedGradPlan, '1To5');
      else if (creditGaps < -5.5 && creditGaps >= -11.0) setValues(studentId, mappedGradPlan, '5To11');
      else if (creditGaps < -11.0 && creditGaps >= -16.5) setValues(studentId, mappedGradPlan, '11To16');
      else if (creditGaps < -16.51) setValues(studentId, mappedGradPlan, '16+');
    });
    return creditGapThresholdBuckets;
  }
}
